/**
 * Pakiet vertykalny `fashion` — buty, torebki, akcesoria
 * (docs/VERTICALS.md § FashionParams).
 *
 * Jeden wpis w `paramDescriptors` = jeden parametr w panelu UI + jeden fragment
 * promptu. Kompilator nie wie nic o modzie; wszystko, co wie, jest tutaj.
 *
 * Regula podzialu (docs/VERTICALS.md § Regula podzialu):
 *   `hard`  = to, za co dzial prawny lub brandowy klienta odrzuci asset
 *             (tozsamosc produktu, kolor okuc, kolorystyka, sylwetka, logo)
 *             -> sekcje CONSTRAINTS / DO_NOT + check QA + checklista approvera,
 *   `soft`  = kwestie gustu -> sekcja VERTICAL.
 *
 * Listy wartosci i schemat Zoda mieszkaja w `./fashion-params` (lisc bez
 * zaleznosci — patrz komentarz tam) i sa STAD RE-EKSPORTOWANE: konsumenci
 * importuja z `./fashion`.
 *
 * Determinizm: zero `Date`, `Math.random()`, zero iteracji po `Object.keys`
 * bez sortowania, liczby przez `formatDecimal` / `formatDegrees` / `formatPercent`.
 */

import {
  QA_DEFAULT_TIERS,
  registerVerticalPack,
  type CaptionGuardrails,
  type ParamDescriptor,
  type QaCheck,
  type VerticalPack,
} from './registry';
import {
  asBoolean,
  asEnumList,
  asEnumValue,
  asNumberValue,
  formatDegrees,
  formatPercent,
  humanizeToken,
  joinList,
  joinPhrases,
  pickSliderBand,
} from './common';
import {
  FASHION_ARRANGEMENTS,
  FASHION_BODY_CROPS,
  FASHION_CLOSURE_STATES,
  FASHION_FLOATING_SUPPORTS,
  FASHION_HARDWARE_ACCENTS,
  FASHION_HERO_ANGLES,
  FASHION_INTERIOR_VISIBLE_ANGLES,
  FASHION_LACE_STYLINGS,
  FASHION_LOGO_POLICIES,
  FASHION_MOTION_CUES,
  FASHION_PROP_KIT,
  FASHION_SHEENS,
  FASHION_SOCK_STYLINGS,
  FASHION_SOLE_VISIBLE_ANGLES,
  FASHION_STRAP_STATES,
  FASHION_SUPPORTS,
  FASHION_SURFACE_PRESETS,
  FASHION_TALENT_IDENTITY_SOURCES,
  FASHION_TEXTURES,
  FASHION_TROUSER_BREAKS,
  FASHION_WEAR_STATES,
  FASHION_WORN_STATES,
  FashionHandModelDetailsSchema,
  FashionLevitationSchema,
  FashionParamsSchema,
  type FashionArrangement,
  type FashionBodyCrop,
  type FashionClosureState,
  type FashionHardwareAccent,
  type FashionHeroAngle,
  type FashionLaceStyling,
  type FashionMotionCue,
  type FashionPropKitItem,
  type FashionSheen,
  type FashionSockStyling,
  type FashionStrapState,
  type FashionSupport,
  type FashionSurfacePreset,
  type FashionTexture,
  type FashionTrouserBreak,
  type FashionWearState,
  type FashionWornState,
} from './fashion-params';

export * from './fashion-params';

/** `generations.template_version` dla tego pakietu. */
export const FASHION_TEMPLATE_VERSION = 'fashion@1';

// ─────────────────────────────────────────────────────────────────────────────
// Tabele fraz (to jest miejsce iterowane przy poprawianiu jakosci promptow)
// ─────────────────────────────────────────────────────────────────────────────

export const FASHION_SURFACE_PRESET_PHRASES: Readonly<Record<FashionSurfacePreset, string>> = {
  seamless_white: 'a seamless pure white studio surface',
  seamless_tonal: 'a seamless tonal studio surface matching the product palette',
  travertine: 'a travertine stone slab with soft pitted texture',
  brushed_steel: 'a brushed steel plate with fine directional grain',
  terrazzo: 'a terrazzo slab with fine mixed aggregate chips',
  raw_concrete: 'a raw poured concrete surface with subtle pitting',
  linen: 'a natural linen cloth with a soft irregular weave',
  oak_plank: 'an oak plank with open grain and warm tone',
  glass_riser: 'a clear glass riser with crisp reflections',
  sand: 'fine dry sand with shallow wind ripples',
  water_shallow: 'a shallow film of still water',
  studio_paper_sweep: 'a studio paper sweep curving up behind the product',
};

const HERO_ANGLE_PHRASES: Readonly<Record<FashionHeroAngle, string>> = {
  three_quarter_front: 'a three-quarter front view of the product',
  profile_lateral: 'a straight lateral profile view, outer side towards the camera',
  profile_medial: 'a straight medial profile view, inner side towards the camera',
  top_down_flat: 'a flat top-down view of the product',
  '45_elevated': 'a 45-degree elevated view of the product',
  heel_back: 'a view from directly behind the heel',
  interior_open: 'a view into the open interior of the product',
  sole_up: 'the product tipped so the outsole faces the camera',
  detail_hardware: 'a tight detail view centred on the hardware',
};

const ARRANGEMENT_PHRASES: Readonly<Record<FashionArrangement, string>> = {
  single: 'a single unit in frame',
  mirrored_pair: 'a mirrored pair, left and right symmetrically placed',
  stacked_pair: 'a pair stacked one on the other',
  one_upright_one_flat: 'a pair with one unit upright and one laid flat',
  fanned_row: 'several units fanned out in a row',
  nested_trio: 'three units nested into each other',
  exploded_components: 'the components separated and laid out as an exploded view',
  family_lineup: 'the full family lined up side by side',
};

const SUPPORT_PHRASES: Readonly<Record<FashionSupport, string>> = {
  invisible_stand: 'held up by an invisible stand, no support visible in the image',
  acrylic_riser: 'resting on a clear acrylic riser',
  levitation_float: 'floating freely above the surface with no support',
  leaning_block: 'leaning against a plain block',
  tissue_stuffed: 'stuffed with tissue to hold a full, showroom shape',
  ghost_mannequin: 'shaped as if worn, with the mannequin removed',
  hanging_strap: 'hanging from its own strap',
  magnetic_hover: 'hovering as if magnetically suspended',
};

const STRAP_STATE_PHRASES: Readonly<Record<FashionStrapState, string>> = {
  down: 'strap hanging down naturally',
  over_shoulder: 'strap lifted over the shoulder',
  held_up: 'strap held up in the hand',
  coiled: 'strap coiled neatly beside the body of the bag',
  crossbody: 'strap arranged crossbody',
  knotted: 'strap knotted for a shorter drop',
};

const LACE_STYLING_PHRASES: Readonly<Record<FashionLaceStyling, string>> = {
  tied_neat: 'laces tied in a neat, symmetrical bow',
  loose_draped: 'laces loosely draped',
  tucked_in: 'laces tucked inside the shoe',
  unlaced: 'laces fully removed, eyelets empty',
  double_bow: 'laces tied in a double bow',
};

const CLOSURE_STATE_PHRASES: Readonly<Record<FashionClosureState, string>> = {
  closed: 'closure fully closed',
  half_open: 'closure half open',
  fully_open: 'closure fully open',
  flap_lifted: 'flap lifted to reveal the closure beneath',
};

const TEXTURE_PHRASES: Readonly<Record<FashionTexture, string>> = {
  leather_grain: 'natural leather grain',
  full_grain_pull_up: 'full-grain pull-up leather with tonal variation',
  suede_nap: 'velvety suede nap',
  nubuck: 'fine brushed nubuck',
  patent_specular: 'patent leather with sharp specular reflections',
  canvas_weave: 'visible canvas weave',
  knit_mesh: 'engineered knit mesh',
  croc_emboss: 'crocodile-embossed relief',
  saffiano_cross: 'saffiano cross-hatch texture',
  woven_intrecciato: 'hand-woven intrecciato panels',
  ripstop: 'technical ripstop grid',
  shearling: 'dense shearling pile',
};

const SHEEN_PHRASES: Readonly<Record<FashionSheen, string>> = {
  matte: 'a matte finish with no specular sheen',
  satin: 'a satin finish with a soft broad sheen',
  high_gloss: 'a high-gloss finish with bright specular highlights',
};

const HARDWARE_ACCENT_PHRASES: Readonly<Record<FashionHardwareAccent, string>> = {
  gold: 'polished gold',
  brass: 'warm brass',
  silver: 'bright silver',
  gunmetal: 'dark gunmetal',
  matte_black: 'matte black',
  none: 'exactly as shown in Reference 1',
};

const WEAR_STATE_PHRASES: Readonly<Record<FashionWearState, string>> = {
  pristine_new: 'the product is factory-new and pristine, no creases or scuffs',
  lightly_worn: 'the product shows light natural wear and gentle flex creases',
  vintage_patina: 'the product shows a deep vintage patina with softened edges',
};

const WORN_STATE_PHRASES: Readonly<Record<FashionWornState, string>> = {
  unworn: 'the product is not worn by anyone',
  on_foot: 'the product is worn on the foot, standing still',
  on_foot_mid_stride: 'the product is worn on the foot mid-stride',
  in_hand: 'the product is held in the hand',
  over_forearm: 'the product is carried over the forearm',
  on_shoulder: 'the product is carried on the shoulder',
  crossbody_on_torso: 'the product is worn crossbody against the torso',
  seated_step: 'the product is worn while seated on a step',
};

const BODY_CROP_PHRASES: Readonly<Record<FashionBodyCrop, string>> = {
  feet_ankle: 'cropped at the ankle, feet only',
  calf_down: 'cropped mid-calf',
  knee_down: 'cropped at the knee',
  hands_only: 'hands only in frame',
  torso_hip: 'cropped from torso to hip',
  full_figure: 'the full figure in frame',
};

const SOCK_STYLING_PHRASES: Readonly<Record<FashionSockStyling, string>> = {
  bare_ankle: 'bare ankle, no sock',
  no_show: 'invisible no-show socks',
  crew_white: 'white crew socks',
  ribbed_tonal: 'ribbed socks tonal to the product',
  sheer: 'sheer hosiery',
};

const TROUSER_BREAK_PHRASES: Readonly<Record<FashionTrouserBreak, string>> = {
  cropped: 'cropped trousers well above the shoe',
  cuffed_denim: 'cuffed denim resting just above the shoe',
  wide_leg_covering: 'wide-leg trousers partially covering the shoe',
  none: 'no trousers in frame',
};

const MOTION_CUE_PHRASES: Readonly<Record<FashionMotionCue, string>> = {
  static: 'everything perfectly still',
  fabric_wind_light: 'a light breeze lifting the fabric slightly',
  fabric_wind_strong: 'a strong wind sweeping the fabric into motion',
  dust_kick: 'a small kick of dust frozen in the air',
  ribbon_swirl: 'a ribbon swirling through the frame',
};

const PROP_KIT_PHRASES: Readonly<Record<FashionPropKitItem, string>> = {
  dust_bag: 'the branded dust bag',
  shoe_box: 'the product box',
  tissue_paper: 'crumpled tissue paper',
  denim_cuff: 'a folded denim cuff',
  jewelry: 'a few pieces of fine jewellery',
  sunglasses: 'a pair of sunglasses',
  perfume_bottle: 'a perfume bottle',
  dried_botanical: 'a dried botanical stem',
  stone_slab: 'a small stone slab',
  fabric_swatch: 'a fabric swatch',
  shoe_tree: 'a wooden shoe tree',
};

/** Pasma "master diala" `editorialLevel`: `pure_catalog ↔ campaign_editorial`. */
export const EDITORIAL_LEVEL_BANDS: readonly string[] = [
  'strict catalogue treatment: clean, neutral, no atmosphere, no styling props',
  'catalogue treatment with a single restrained styling accent',
  'elevated commercial treatment: subtle atmosphere and considered styling',
  'editorial treatment: expressive light, visible atmosphere and richer styling',
  'full campaign editorial treatment: bold light, strong atmosphere and layered styling',
];

/**
 * TODO(spec): VERTICALS.md oznacza `heroAngle` jako "H-ish". Traktuje go jako
 * `soft`, bo kat ujecia nie jest podstawa do odrzucenia assetu przez dzial
 * prawny ani brandowy (regula podzialu w VERTICALS.md), a twarde parametry maja
 * wg dokumentacji dostawac numerowana klauzule + wpis DO_NOT + check QA — nic z
 * tego nie ma sensu dla kata. Do potwierdzenia z autorem specyfikacji.
 */
const heroAngle: ParamDescriptor = {
  label: 'Hero angle',
  control: 'enum',
  options: FASHION_HERO_ANGLES,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const angle = asEnumValue(FASHION_HERO_ANGLES, value);
    return angle === null ? null : `Show ${HERO_ANGLE_PHRASES[angle]}`;
  },
  conflictsWith: ['interiorVisible', 'soleVisible'],
  defaultsByProductType: {
    sneaker: 'three_quarter_front',
    boot: 'three_quarter_front',
    sandal: 'profile_lateral',
    handbag: 'three_quarter_front',
    wallet: '45_elevated',
    belt: 'top_down_flat',
  },
};

const arrangement: ParamDescriptor = {
  label: 'Arrangement',
  control: 'enum',
  options: FASHION_ARRANGEMENTS,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_ARRANGEMENTS, value);
    return key === null ? null : ARRANGEMENT_PHRASES[key];
  },
};

const support: ParamDescriptor = {
  label: 'Support',
  control: 'enum',
  options: FASHION_SUPPORTS,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_SUPPORTS, value);
    return key === null ? null : `The product is ${SUPPORT_PHRASES[key]}`;
  },
  conflictsWith: ['levitation'],
};

const levitation: ParamDescriptor = {
  /** Obiekt zlozony — patrz TODO(spec) przy `PARAM_CONTROLS` w `pharma.ts`. */
  label: 'Levitation',
  control: 'bool',
  constraintClass: 'soft',
  promptFragment: (value) => {
    const parsed = FashionLevitationSchema.safeParse(value);
    if (!parsed.success) return null;
    const levitationValue = parsed.data;
    if (!levitationValue.enabled) return null;
    const height =
      levitationValue.heightPct === undefined
        ? null
        : `about ${formatPercent(levitationValue.heightPct)} of the frame height above the surface`;
    const tilt =
      levitationValue.tiltDeg === undefined || levitationValue.tiltDeg === 0
        ? null
        : `tilted ${formatDegrees(Math.abs(levitationValue.tiltDeg))} ${levitationValue.tiltDeg > 0 ? 'nose up' : 'nose down'}`;
    const anchor =
      levitationValue.shadowAnchor === undefined
        ? null
        : levitationValue.shadowAnchor === 'none'
          ? 'no shadow beneath it'
          : levitationValue.shadowAnchor === 'soft_contact'
            ? 'a soft contact shadow directly beneath it'
            : 'only a cast shadow, with no contact point';
    const blur = levitationValue.motionBlur === true ? 'a trace of motion blur along the leading edge' : null;
    return joinPhrases(['The product floats in mid air with no visible support', height, tilt, anchor, blur], ', ');
  },
  conflictsWith: ['support'],
  conflicts: (value, params) => {
    if (typeof value !== 'object' || value === null) return [];
    if ((value as { enabled?: unknown }).enabled !== true) return [];
    const support = asEnumValue(FASHION_SUPPORTS, params.support);
    if (support === null || FASHION_FLOATING_SUPPORTS.includes(support)) return [];
    return [
      {
        with: 'support',
        message: `levitation is enabled, but support "${support}" implies visible physical contact`,
      },
    ];
  },
};

const shapeIntegrity: ParamDescriptor = {
  label: 'Shape integrity lock',
  control: 'bool',
  constraintClass: 'hard',
  /**
   * Klauzula DOSLOWNIE z docs/VERTICALS.md. Domysl (wartosc `undefined`) to
   * WLACZONE — parametr twardy nie moze zniknac przez pominiecie pola.
   */
  promptFragment: (value) => {
    if (asBoolean(value) === false) return null;
    return 'Silhouette must not be deformed; toe box, heel counter and stitch lines remain straight and true to Reference 1';
  },
  forbidFragments: (value) =>
    asBoolean(value) === false
      ? []
      : ['deformed silhouette', 'warped or wavy stitch lines', 'collapsed toe box', 'melted or bent heel counter'],
};

const soleVisible: ParamDescriptor = {
  label: 'Outsole visible',
  control: 'bool',
  constraintClass: 'hard',
  promptFragment: (value) => {
    if (asBoolean(value) !== true) return null;
    return 'The outsole tread pattern is fully visible and in sharp focus';
  },
  forbidFragments: (value) => (asBoolean(value) === true ? ['outsole hidden or cropped out of frame'] : []),
  conflictsWith: ['heroAngle'],
  conflicts: (value, params) => {
    if (asBoolean(value) !== true) return [];
    const angle = asEnumValue(FASHION_HERO_ANGLES, params.heroAngle);
    if (angle === null || FASHION_SOLE_VISIBLE_ANGLES.includes(angle)) return [];
    return [
      {
        with: 'heroAngle',
        message: `outsole must be visible, but hero angle "${angle}" does not show the sole`,
      },
    ];
  },
  defaultsByProductType: { sneaker: false, boot: false, sandal: false },
};

const interiorVisible: ParamDescriptor = {
  label: 'Interior visible',
  control: 'bool',
  constraintClass: 'hard',
  promptFragment: (value) => {
    if (asBoolean(value) !== true) return null;
    return 'The bag interior lining and one internal pocket are visible';
  },
  forbidFragments: (value) => (asBoolean(value) === true ? ['interior hidden by a closed flap'] : []),
  conflictsWith: ['closureState', 'heroAngle'],
  conflicts: (value, params) => {
    if (asBoolean(value) !== true) return [];
    const hits: { with: string; message: string }[] = [];

    const closure = asEnumValue(FASHION_CLOSURE_STATES, params.closureState);
    if (closure === 'closed') {
      hits.push({
        with: 'closureState',
        message: 'interior must be visible, but the closure is set to fully closed',
      });
    }

    const angle = asEnumValue(FASHION_HERO_ANGLES, params.heroAngle);
    if (angle !== null && !FASHION_INTERIOR_VISIBLE_ANGLES.includes(angle)) {
      hits.push({
        with: 'heroAngle',
        message: `interior must be visible, but hero angle "${angle}" cannot see inside the bag`,
      });
    }

    return hits;
  },
  defaultsByProductType: { handbag: false, tote: false },
};

const strapState: ParamDescriptor = {
  label: 'Strap state',
  control: 'enum',
  options: FASHION_STRAP_STATES,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_STRAP_STATES, value);
    return key === null ? null : STRAP_STATE_PHRASES[key];
  },
};

const laceStyling: ParamDescriptor = {
  label: 'Lace styling',
  control: 'enum',
  options: FASHION_LACE_STYLINGS,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_LACE_STYLINGS, value);
    return key === null ? null : LACE_STYLING_PHRASES[key];
  },
};

const closureState: ParamDescriptor = {
  label: 'Closure state',
  control: 'enum',
  options: FASHION_CLOSURE_STATES,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_CLOSURE_STATES, value);
    return key === null ? null : CLOSURE_STATE_PHRASES[key];
  },
  conflictsWith: ['interiorVisible'],
};

const textureEmphasis: ParamDescriptor = {
  label: 'Texture emphasis',
  control: 'multi',
  options: FASHION_TEXTURES,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const keys = asEnumList(FASHION_TEXTURES, value);
    if (keys === null || keys.length === 0) return null;
    return `Render the material texture legibly: ${joinList(keys.map((key) => TEXTURE_PHRASES[key]))}`;
  },
};

const sheen: ParamDescriptor = {
  label: 'Sheen',
  control: 'enum',
  options: FASHION_SHEENS,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_SHEENS, value);
    return key === null ? null : `The surface reads with ${SHEEN_PHRASES[key]}`;
  },
};

const specularControlPct: ParamDescriptor = {
  label: 'Specular control',
  control: 'slider',
  constraintClass: 'soft',
  promptFragment: (value) => {
    const pct = asNumberValue(value);
    if (pct === null) return null;
    const band = pickSliderBand(
      [
        'let specular highlights run bright and glossy',
        'keep specular highlights lively but controlled',
        'tame specular highlights with broad diffusion',
        'suppress specular highlights almost entirely',
      ],
      pct / 100,
    );
    return `Specular control at ${formatPercent(pct)}: ${band}`;
  },
};

const hardwareAccent: ParamDescriptor = {
  label: 'Hardware accent',
  control: 'enum',
  options: FASHION_HARDWARE_ACCENTS,
  constraintClass: 'hard',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_HARDWARE_ACCENTS, value) ?? 'none';
    return `Hardware colour must remain ${HARDWARE_ACCENT_PHRASES[key]}; do not change buckle or zipper finish`;
  },
  forbidFragments: () => ['recoloured hardware', 'changed buckle or zipper finish', 'added or removed hardware'],
};

const logoPolicy: ParamDescriptor = {
  label: 'Logo policy',
  control: 'enum',
  options: FASHION_LOGO_POLICIES,
  constraintClass: 'hard',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_LOGO_POLICIES, value) ?? 'must_be_sharp';
    if (key === 'must_be_sharp') {
      return 'The brand logo must be rendered sharp, complete and identical to Reference 1';
    }
    if (key === 'subtle_visible') {
      return 'The brand logo is visible but understated, and still identical to Reference 1';
    }
    return 'The brand logo is turned away from the camera and not visible; do not reconstruct it';
  },
  forbidFragments: (value) => {
    const key = asEnumValue(FASHION_LOGO_POLICIES, value) ?? 'must_be_sharp';
    const shared = ['invented or altered logo', 'misspelled brand name'];
    return key === 'hidden_from_view' ? [...shared, 'logo visible in frame'] : [...shared, 'blurred or cropped logo'];
  },
};

const stitchColorLock: ParamDescriptor = {
  label: 'Stitch colour lock',
  control: 'bool',
  constraintClass: 'hard',
  promptFragment: (value) => {
    if (asBoolean(value) === false) return null;
    return 'Contrast stitching keeps exactly the thread colour and stitch pitch shown in Reference 1';
  },
  forbidFragments: (value) =>
    asBoolean(value) === false ? [] : ['recoloured stitching', 'invented topstitching', 'missing contrast stitching'],
};

const colorwayLock: ParamDescriptor = {
  label: 'Colourway lock',
  control: 'bool',
  constraintClass: 'hard',
  promptFragment: (value) => {
    if (asBoolean(value) === false) return null;
    return 'Colour blocking and panel colours exactly as Reference 1; do not recolour';
  },
  forbidFragments: (value) =>
    asBoolean(value) === false
      ? []
      : ['recoloured panels', 'swapped colour blocking', 'shifted or oversaturated product colours'],
};

const wearState: ParamDescriptor = {
  label: 'Wear state',
  control: 'enum',
  options: FASHION_WEAR_STATES,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_WEAR_STATES, value);
    return key === null ? null : WEAR_STATE_PHRASES[key];
  },
};

const wornState: ParamDescriptor = {
  label: 'Worn state',
  control: 'enum',
  options: FASHION_WORN_STATES,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_WORN_STATES, value);
    return key === null ? null : WORN_STATE_PHRASES[key];
  },
  conflictsWith: ['support'],
  conflicts: (value, params) => {
    const worn = asEnumValue(FASHION_WORN_STATES, value);
    // Podpora studyjna ma sens tylko dla produktu nieuzywanego — noszony produkt
    // trzyma sie ciala, wiec stojak albo wypelnienie sa sprzeczne z ujeciem.
    if (worn === null || worn === 'unworn') return [];
    const support = asEnumValue(FASHION_SUPPORTS, params.support);
    if (support === null) return [];
    return [
      {
        with: 'support',
        message: `product is shown "${worn}" on a person, but a studio support "${support}" is also set`,
      },
    ];
  },
};

const bodyCrop: ParamDescriptor = {
  label: 'Body crop',
  control: 'enum',
  options: FASHION_BODY_CROPS,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_BODY_CROPS, value);
    return key === null ? null : `The figure is ${BODY_CROP_PHRASES[key]}`;
  },
};

const sockStyling: ParamDescriptor = {
  label: 'Sock styling',
  control: 'enum',
  options: FASHION_SOCK_STYLINGS,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_SOCK_STYLINGS, value);
    return key === null ? null : SOCK_STYLING_PHRASES[key];
  },
};

const trouserBreak: ParamDescriptor = {
  label: 'Trouser break',
  control: 'enum',
  options: FASHION_TROUSER_BREAKS,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_TROUSER_BREAKS, value);
    return key === null ? null : TROUSER_BREAK_PHRASES[key];
  },
};

const handModelDetails: ParamDescriptor = {
  label: 'Hand model details',
  control: 'multi',
  constraintClass: 'soft',
  promptFragment: (value) => {
    const parsed = FashionHandModelDetailsSchema.safeParse(value);
    if (!parsed.success) return null;
    const details = parsed.data;
    const nails = details.nails === undefined ? null : `${humanizeToken(details.nails)} nails`;
    const jewelry =
      details.jewelry === undefined ? null : details.jewelry ? 'a few fine rings' : 'no rings or bracelets';
    const watch = details.watch === undefined ? null : details.watch ? 'a slim wristwatch' : 'no wristwatch';
    const parts = joinPhrases([nails, jewelry, watch], ', ');
    return parts === null ? null : `The hands show ${parts}`;
  },
};

const talentIdentitySource: ParamDescriptor = {
  label: 'Talent identity source',
  control: 'enum',
  options: FASHION_TALENT_IDENTITY_SOURCES,
  constraintClass: 'hard',
  /**
   * Decyzja swiadoma (docs/VERTICALS.md): NIGDY nie opisujemy tekstem
   * etnicznosci ani wieku osoby. Fragment jest emitowany takze przy `undefined`,
   * bo `reference_image_only` jest jedyna dopuszczalna polityka.
   */
  promptFragment: () =>
    'Any person in frame must match the person shown in the talent reference image; do not describe or invent their appearance',
  forbidFragments: () => [
    'invented facial features',
    'described ethnicity or age of the person',
    'a different person than in the talent reference',
  ],
};

const surfacePreset: ParamDescriptor = {
  label: 'Surface preset',
  control: 'enum',
  options: FASHION_SURFACE_PRESETS,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_SURFACE_PRESETS, value);
    return key === null ? null : `The product sits on ${FASHION_SURFACE_PRESET_PHRASES[key]}`;
  },
};

const propKit: ParamDescriptor = {
  label: 'Prop kit',
  control: 'multi',
  options: FASHION_PROP_KIT,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const keys = asEnumList(FASHION_PROP_KIT, value);
    if (keys === null || keys.length === 0) return null;
    return `Styling props in frame: ${joinList(keys.map((key) => PROP_KIT_PHRASES[key]))}, all clearly secondary to the product`;
  },
};

const motionCue: ParamDescriptor = {
  label: 'Motion cue',
  control: 'enum',
  options: FASHION_MOTION_CUES,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(FASHION_MOTION_CUES, value);
    return key === null ? null : MOTION_CUE_PHRASES[key];
  },
};

const editorialLevel: ParamDescriptor = {
  label: 'Editorial level',
  control: 'slider',
  constraintClass: 'soft',
  /** "Master dial": jedna galka biasujaca grade, atmosfere i gestosc propsow. */
  promptFragment: (value) => {
    const level = asNumberValue(value);
    if (level === null) return null;
    return pickSliderBand(EDITORIAL_LEVEL_BANDS, level);
  },
};

export const FASHION_PARAM_DESCRIPTORS: Readonly<Record<string, ParamDescriptor>> = {
  arrangement,
  bodyCrop,
  closureState,
  colorwayLock,
  editorialLevel,
  handModelDetails,
  hardwareAccent,
  heroAngle,
  interiorVisible,
  laceStyling,
  levitation,
  logoPolicy,
  motionCue,
  propKit,
  shapeIntegrity,
  sheen,
  sockStyling,
  soleVisible,
  specularControlPct,
  stitchColorLock,
  strapState,
  support,
  surfacePreset,
  talentIdentitySource,
  textureEmphasis,
  trouserBreak,
  wearState,
  wornState,
};

// ─────────────────────────────────────────────────────────────────────────────
// Twarde klauzule domyslne i lista negatywna
// ─────────────────────────────────────────────────────────────────────────────

export const FASHION_DEFAULT_CONSTRAINTS: readonly string[] = [
  'The product must be identical to Reference 1 in silhouette, proportions, panel layout, materials and colour.',
  'Do not add, remove, resize or restyle any product component.',
  'Do not render any brand name, logo or text that is not present in the references.',
  'Left and right units of a pair must be mirror-consistent, with matching materials and hardware.',
];

export const FASHION_DEFAULT_FORBID: readonly string[] = [
  'distorted or asymmetric product geometry',
  'extra or missing straps, laces or eyelets',
  'invented logos or brand text',
  'text overlay, caption or watermark',
  'plastic-looking or waxy leather',
  'visible mannequin, stand or wire',
  'duplicated or mismatched pair',
  'oversaturated or shifted product colours',
  'dirty, dusty or damaged product (unless a worn state was requested)',
  'extra fingers or malformed hands',
];

// ─────────────────────────────────────────────────────────────────────────────
// Checki QA (docs/VERTICALS.md § Checki QA per vertykal)
// ─────────────────────────────────────────────────────────────────────────────

export const FASHION_QA_CHECKS: readonly QaCheck[] = [
  {
    id: 'silhouette_integrity',
    label: 'Silhouette matches the primary reference',
    kind: 'vision',
    tiers: QA_DEFAULT_TIERS,
    severity: 'blocking',
    comparesToRefRole: 'primary',
    visionInstruction:
      'Compare the product silhouette in the generated image with the reference. Report whether the outline, proportions and panel layout match, and list every visible deviation.',
    appliesWhen: (ctx) => asBoolean(ctx.params['shapeIntegrity']) !== false,
  },
  {
    id: 'hardware_color',
    label: 'Hardware colour matches the primary reference',
    kind: 'vision',
    tiers: QA_DEFAULT_TIERS,
    severity: 'blocking',
    comparesToRefRole: 'primary',
    visionInstruction:
      'Compare the metal hardware (buckles, zippers, eyelets, clasps) in the generated image with the reference. Report whether the finish and colour match exactly.',
    appliesWhen: (ctx) => asEnumValue(FASHION_HARDWARE_ACCENTS, ctx.params['hardwareAccent']) !== 'none',
  },
  {
    id: 'no_invented_text',
    label: 'No text that is absent from the references',
    kind: 'vision',
    tiers: QA_DEFAULT_TIERS,
    severity: 'warning',
    comparesToRefRole: 'primary',
    visionInstruction:
      'List every piece of legible text in the generated image and flag anything that is not present in the references.',
  },
  {
    id: 'dimensions_and_sharpness',
    label: 'Output dimensions and sharpness',
    kind: 'image_info',
    tiers: QA_DEFAULT_TIERS,
    severity: 'warning',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Guardraile opisow
// ─────────────────────────────────────────────────────────────────────────────

/**
 * TODO(spec): dokumentacja wylicza `forbiddenVerbs` tylko dla pharmy. Dla mody
 * blokujemy niesprawdzalne obietnice trwalosci i pochodzenia — to jedyna klasa
 * claimow, ktora realnie sciaga na klienta ryzyko prawne w reklamie mody.
 */
export const FASHION_CAPTION_GUARDRAILS: CaptionGuardrails = {
  forbiddenVerbs: [
    'gwarantuje',
    'gwarantowana trwalosc',
    'niezniszczalny',
    'wieczny',
    'najlepszy na swiecie',
    'guarantees',
    'guaranteed',
    'indestructible',
    'lasts forever',
    'best in the world',
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Pakiet
// ─────────────────────────────────────────────────────────────────────────────

export const fashionPack: VerticalPack = {
  id: 'fashion',
  templateVersion: FASHION_TEMPLATE_VERSION,
  paramsSchema: FashionParamsSchema,
  paramDescriptors: FASHION_PARAM_DESCRIPTORS,
  defaultConstraints: FASHION_DEFAULT_CONSTRAINTS,
  defaultForbid: FASHION_DEFAULT_FORBID,
  qaChecks: FASHION_QA_CHECKS,
  captionGuardrails: FASHION_CAPTION_GUARDRAILS,
};

registerVerticalPack(fashionPack);
